import React, { useEffect, useMemo, useState } from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { useParams } from 'react-router-dom';
import DatePicker from 'v3/components/DatePicker';
import Input from 'v3/components/Input';
import Text from 'v3/components/Text';
import Radio from 'v3/components/Radio';
import Select from 'v3/components/Select';
import { fetchAnttTypes, fetchLinkShippingCompany } from 'utils/fetches';
import { filterRntrcTypes } from 'v3/utils/rntrcValidation';
import { useShippingCompany } from '../context';
import { CNPJ_MASK, CPF_MASK, PHONE_MASK, PIS_MASK } from 'v3/utils/masks';

const SIMPLES_NACIONAL_OPTIONS = [
  { value: true, label: 'Sim' },
  { value: false, label: 'Não' },
];

export default function ShippingCompanyData() {
  const shippingCompany = useShippingCompany();
  const isCompany = shippingCompany?.data?.personType?.name === 'Jurídica';

  const [linkOption, setLinkOption] = useState([]);
  const [rntrcTypes, setRntrcTypes] = useState([]);

  const params = useParams();

  useEffect(() => {
    fetchLinkShippingCompany().then(setLinkOption);
    fetchAnttTypes().then((types) => setRntrcTypes(filterRntrcTypes(types)));

    if (!params.id)
      shippingCompany.setData({
        personType: { name: 'Jurídica' },
        cgccpf: '',
        ie: '',
        social_name: '',
        fantasy_name: '',
        rg: '',
        is_driver: false,
        email: '',
        phone: '',
        rntrc_number: '',
        rntrc_issue_date: '',
        rntrc_exp_date: '',
        rntrc_type: null,
        pispasep: '',
        shipment_link: null,
      });
  }, []);

  const documentInput = useMemo(() => {
    if (shippingCompany?.data?.personType?.name === 'Jurídica') {
      return {
        label: 'CNPJ *',
        mask: CNPJ_MASK,
      };
    }
    return {
      label: 'CPF *',
      mask: CPF_MASK,
    };
  }, [shippingCompany?.data?.personType]);

  // Mantém como opção um tipo legado fora de ETC/TAC/CTC, para não apagá-lo ao editar
  const rntrcTypeOptions = useMemo(() => {
    const currentType = shippingCompany?.data?.rntrc_type;
    if (currentType?.name && !rntrcTypes.some((type) => type.name === currentType.name)) {
      return [...rntrcTypes, currentType];
    }
    return rntrcTypes;
  }, [rntrcTypes, shippingCompany?.data?.rntrc_type]);

  useEffect(() => {
    if (shippingCompany?.data?.personType?.name === 'Física') {
      shippingCompany.setData({ ie: null, fantasy_name: null });
    } else if (shippingCompany?.data?.personType?.name === 'Jurídica') {
      shippingCompany.setData({ pispasep: null });
    }
  }, [shippingCompany?.data?.personType]);

  return (
    <Row className="form">
      <Col md={12} className="mb-3">
        <Radio.Group
          label="Tipo de pessoa *"
          onChange={({ target }) => {
            if (target.value === 'Jurídica') {
              shippingCompany.setData({
                is_driver: false,
              });
            }
            shippingCompany.setData({
              personType: {
                ...shippingCompany?.data?.personType,
                name: target.value,
              },
              cgccpf: '',
            });
          }}
          value={shippingCompany.data?.personType?.name}
          horizontal
        >
          <Radio value="Jurídica">
            <Text color="dark" type="label">
              Jurídica
            </Text>
          </Radio>
          <Radio value="Física">
            <Text color="dark" type="label">
              Física
            </Text>
          </Radio>
        </Radio.Group>
      </Col>
      <Col xs={6} md={6}>
        <Input
          label={documentInput.label}
          value={shippingCompany?.data?.cgccpf}
          onChange={(e) => shippingCompany.setData({ cgccpf: e.target.value })}
          onBlur={(e) => shippingCompany.setData({ cgccpf: e.target.value })}
          masked
          mask={documentInput.mask}
          error={shippingCompany.errors?.['data.cgccpf']}
          guide={false}
        />
      </Col>
      {!isCompany ? (
        <Col xs={6} md={6}>
          <Input
            label="Pis/Pasep *"
            value={shippingCompany?.data?.pispasep}
            onChange={(e) => shippingCompany.setData({ pispasep: e.target.value })}
            masked
            mask={PIS_MASK}
            error={shippingCompany.errors?.['data.pispasep']}
            guide={false}
          />
        </Col>
      ) : null}

      {shippingCompany?.data?.personType?.name === 'Jurídica' && (
        <Col xs={12} md={6}>
          <Input
            label={params.id ? 'Inscrição estadual *' : 'Inscrição estadual'}
            value={shippingCompany?.data?.ie}
            onChange={(e) => shippingCompany.setData({ ie: e.target.value })}
            onBlur={(e) => shippingCompany.setData({ ie: e.target.value })}
            error={shippingCompany.errors?.['data.ie']}
          />
        </Col>
      )}
      <Col xs={12}>
        <Input
          label={
            shippingCompany?.data?.personType?.name === 'Jurídica' ? 'Razão social *' : 'Nome *'
          }
          value={shippingCompany?.data?.social_name}
          onChange={(e) => shippingCompany.setData({ social_name: e.target.value })}
          onBlur={(e) => shippingCompany.setData({ social_name: e.target.value })}
          error={shippingCompany.errors?.['data.social_name']}
        />
      </Col>
      {shippingCompany?.data?.personType?.name === 'Jurídica' && (
        <Col xs={12}>
          <Input
            label={params.id ? 'Nome fantasia *' : 'Nome fantasia'}
            value={shippingCompany?.data?.fantasy_name}
            onChange={(e) => shippingCompany.setData({ fantasy_name: e.target.value })}
            onBlur={(e) => shippingCompany.setData({ fantasy_name: e.target.value })}
            error={shippingCompany?.errors?.['data.fantasy_name']}
          />
        </Col>
      )}
      <Col xs={12} md={6}>
        <Input
          label="RNTRC"
          value={shippingCompany?.data?.rntrc_number}
          onChange={(e) => shippingCompany.setData({ rntrc_number: e.target.value })}
          error={shippingCompany.errors?.['data.rntrc_number']}
        />
      </Col>
      <Col xs={12} md={6}>
        <Select
          label="Tipo de RNTRC"
          value={shippingCompany?.data?.rntrc_type}
          options={rntrcTypeOptions}
          getOptionLabel={(option) => option.name}
          getOptionValue={(option) => option.name}
          onChange={(value) => shippingCompany.setData({ rntrc_type: value })}
          error={shippingCompany.errors?.['data.rntrc_type']}
        />
      </Col>
      <Col xs={12} md={6}>
        <DatePicker
          guide
          label="Data de emissão RNTRC"
          value={shippingCompany?.data?.rntrc_issue_date}
          onChange={(e) => shippingCompany.setData({ rntrc_issue_date: e.target.value })}
          error={shippingCompany.errors?.['data.rntrc_issue_date']}
        />
      </Col>
      <Col xs={12} md={6}>
        <DatePicker
          guide
          label="Validade RNTRC"
          value={shippingCompany?.data?.rntrc_exp_date}
          onChange={(e) => shippingCompany.setData({ rntrc_exp_date: e.target.value })}
          error={shippingCompany.errors?.['data.rntrc_exp_date']}
        />
      </Col>
      {params.id && (
        <>
          <Col xs={12} md={6}>
            <Select
              label="Optante Simples Nacional"
              placeholder="Não informado"
              options={SIMPLES_NACIONAL_OPTIONS}
              value={
                SIMPLES_NACIONAL_OPTIONS.find(
                  (option) => option.value === shippingCompany?.data?.simples_nacional,
                ) || null
              }
              onChange={(option) =>
                shippingCompany.setData({ simples_nacional: option ? option.value : null })
              }
              disableSearch
              error={shippingCompany.errors?.['data.simples_nacional']}
            />
          </Col>
          <Col xs={12} md={6}>
            <DatePicker
              guide
              label="Início emissão contra CT-e"
              value={shippingCompany?.data?.emission_start_date}
              onChange={(e) => shippingCompany.setData({ emission_start_date: e.target.value })}
              error={shippingCompany.errors?.['data.emission_start_date']}
            />
          </Col>
        </>
      )}
      <Col md={6}>
        <Radio.Group
          label="É motorista?"
          value={!!shippingCompany?.data?.is_driver}
          onChange={({ target }) => {
            const isDriver = target.value === true || target.value === 'true';
            shippingCompany.setData({
              is_driver: isDriver,
            });
          }}
          disabled={shippingCompany?.data?.personType?.name === 'Jurídica'}
          horizontal
        >
          <Radio value>
            <Text color="dark" type="label">
              Sim
            </Text>
          </Radio>
          <Radio value={false}>
            <Text color="dark" type="label">
              Não
            </Text>
          </Radio>
        </Radio.Group>
      </Col>
      <Col md={6}>
        <Select
          label="Vinculo transportador"
          value={shippingCompany?.data?.shipment_link}
          options={linkOption}
          getOptionLabel={(option) => option.name}
          getOptionValue={(option) => option.id}
          onChange={(value) => {
            shippingCompany.setData({ shipment_link: value });
          }}
        />
      </Col>
      <Col xs={12} md={6} className="mt-3">
        <Input
          label="Telefone *"
          value={shippingCompany.data?.phone}
          onChange={(e) => shippingCompany.setData({ phone: e.target.value })}
          onBlur={(e) => shippingCompany.setData({ phone: e.target.value })}
          masked
          mask={PHONE_MASK}
          error={shippingCompany?.errors?.['data.phone']}
          guide={false}
        />
      </Col>
      <Col xs={12}>
        <Input
          label="E-mail"
          value={shippingCompany?.data?.email}
          onChange={(e) => shippingCompany.setData({ email: e.target.value })}
          error={shippingCompany.errors?.['data.email']}
        />
      </Col>
    </Row>
  );
}
